/**
 * A CHAVE do cartão de boas-vindas do primeiro contato — e o detector que diz
 * se um turno já a carrega.
 *
 * ★ **Por que a chave mora numa constante, e num arquivo só dela.**
 *
 * O backend passou a tratar o primeiro contato como um TURNO próprio
 * (`core/boas_vindas_do_servidor.py`): a pergunta é persistida, a resposta vem
 * sem chamar modelo nenhum, e o que diz "este turno É a boas-vindas" é uma
 * parte de tool marcada com esta chave. **O contrato ainda não está fechado** —
 * o nome pode mudar antes do merge. Com a chave literal espalhada pelo
 * registry e pelo gate de duplicação, trocá-la seria caçar strings iguais em
 * arquivos diferentes; com ela aqui, é UMA linha.
 *
 * ★ **Zero imports, de propósito.** Este arquivo é folha pelo mesmo motivo de
 * `chave-de-tool-de-front.js`: quem precisa dele são dois lados que não podem
 * se importar — o registry parcial da plataforma (`ui-registry/plataforma.js`,
 * que puxa o catálogo de navegação inteiro) e a fiação da tela
 * (`views/chat/view-chat-boas-vindas.mixin.js`). Um import do registry a
 * partir da view fecharia o ciclo que derrubou 8 suítes em 26/08 ([T010]).
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */

/**
 * A chave que o backend manda em `ui.component` da parte do turno de primeiro
 * contato (`COMPONENTE_BOAS_VINDAS`, `core/boas_vindas_do_servidor.py`).
 *
 * ⚠️ Espelho MANUAL do backend, como todo `ui.component` deste módulo. Trocar
 * aqui e no backend é o mesmo gesto; trocar só de um lado faz o cartão cair no
 * `fallback-card` com o motivo `chave_desconhecida` — visível na tela, que é
 * exatamente o desfecho que o registry existe para dar.
 */
export const CHAVE_DO_CARTAO_DE_BOAS_VINDAS = 'plataforma/boas-vindas'

/**
 * Esta parte (viva) ou esta chamada (persistida) é o marcador da boas-vindas?
 *
 * O discriminante é `ui.component`, e não o nome da tool: o backend declara que
 * `boas_vindas_primeiro_contato` **não é** uma tool registrada (nenhum modelo a
 * chama), e que o front resolve o cartão por `ui.component` — nunca pelo nome.
 * Ler o nome aqui seria combinar com um campo que o próprio backend diz existir
 * só para quem lê o JSONB.
 *
 * @param {Object} parte Uma parte de `toolParts` ou um item de `tool_calls[]`.
 * @returns {boolean}
 */
export function ehCartaoDeBoasVindas(parte) {
  if (!parte || typeof parte !== 'object') return false
  const ui = parte.ui
  if (!ui || typeof ui !== 'object') return false
  return ui.component === CHAVE_DO_CARTAO_DE_BOAS_VINDAS
}

/**
 * A conversa (ao vivo OU recarregada) já carrega o cartão de boas-vindas?
 *
 * ★ As DUAS origens entram, e é isso que faz o gate valer nos dois momentos: a
 * parte VIVA chega no `tool-output-available` do turno em andamento, e a
 * chamada PERSISTIDA volta em `message.tool_calls[]` quando a conversa é
 * reaberta. Olhar só uma delas deixaria o bloco antigo coexistir com o cartão
 * justamente na metade dos casos — e "aparece duas vezes" é defeito que
 * ninguém reporta como bug de renderização, e sim como "a IA repetiu".
 *
 * @param {{partes?: Array, mensagens?: Array}} contexto
 * @returns {boolean}
 */
export function turnoTemCartaoDeBoasVindas({ partes, mensagens } = {}) {
  const vivas = Array.isArray(partes) ? partes : []
  if (vivas.some(ehCartaoDeBoasVindas)) return true

  const conversa = Array.isArray(mensagens) ? mensagens : []
  return conversa.some((mensagem) => {
    const chamadas = mensagem && mensagem.tool_calls
    if (!Array.isArray(chamadas)) return false
    return chamadas.some(ehCartaoDeBoasVindas)
  })
}
